import { useCallback, useState } from 'react';
import type { Mcp } from '../services/mcpService';
import { filtrarMcpsQueCubren, resolverMcpRedondeo } from '../utils/mcpRedondeo';

/**
 * Resuelve el MCP de redondeo de un grupo/caja.
 * - 1 que cubra → lo retorna
 * - varios que cubran → abre diálogo
 * - ninguno → null (sin abrir diálogo de elección vacía)
 */
export function useMcpRedondeo() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [grupoDialog, setGrupoDialog] = useState(0);
  const [importeAbsDialog, setImporteAbsDialog] = useState(0);
  const [pending, setPending] = useState<{
    resolve: (mcp: Mcp | null) => void;
  } | null>(null);

  const ensureMcpRedondeo = useCallback(
    async (grupo: number, importeAbs?: number): Promise<Mcp | null> => {
      if (!(Number(grupo) > 0)) return null;
      const result = await resolverMcpRedondeo(grupo);
      const abs = Math.abs(Number(importeAbs) || 0);

      if (result.status === 'none') return null;

      const opciones =
        abs > 0 ? filtrarMcpsQueCubren(result.status === 'ok' ? [result.mcp] : result.opciones, abs) : result.status === 'ok' ? [result.mcp] : result.opciones;

      if (opciones.length === 0) return null;
      if (opciones.length === 1) return opciones[0];

      return new Promise<Mcp | null>((resolve) => {
        setPending({ resolve });
        setGrupoDialog(grupo);
        setImporteAbsDialog(abs);
        setDialogOpen(true);
      });
    },
    [],
  );

  const handleSelect = useCallback(
    (mcp: Mcp) => {
      pending?.resolve(mcp);
      setPending(null);
      setDialogOpen(false);
    },
    [pending],
  );

  const handleClose = useCallback(() => {
    pending?.resolve(null);
    setPending(null);
    setDialogOpen(false);
  }, [pending]);

  return {
    ensureMcpRedondeo,
    dialogProps: {
      open: dialogOpen,
      grupo: grupoDialog,
      importeAbsMinimo: importeAbsDialog,
      onClose: handleClose,
      onSelect: handleSelect,
    },
  };
}
